import React, {
  useContext, useEffect, useState
}                                     from 'react'
import {
  Box, Button,
  Checkbox, Container, FormControl,
  FormControlLabel, FormGroup,
  Grid, Link, Paper,
  Stack, TextField, Typography,
}                                     from '@mui/material'
import { useTranslation }             from 'react-i18next'
import { useNavigate, useParams }     from 'react-router-dom'
import { useApi }                     from '../utils/api'
import { useSnackbar }                from 'notistack'
import AlertSimpleDialog              from '../ui/components/alert-simple-dialog'
import Page                           from '../ui/components/Page'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import ShortFormControl               from '../ui/components/short-form-control'
import { AppContext }                 from '../App'
import { CronExpressionParser }       from 'cron-parser'

export default function ServiceEmailPage() {

  const api = useApi()
  const { user } = useContext(AppContext)
  const navigate = useNavigate()
  const { serviceId, serviceEmailId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [service, setService] = useState(null)
  const [values, setValues] = useState({
    cronExpression: '',
    recipients: '',
    onlyCorrectAnswers: false,
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)

  useEffect(() => {
    if (!isNaN(serviceId)) {
      api.service.detail(serviceId).then(data => setService(data)).catch(e => console.error(e))
    }
  }, [serviceId])

  useEffect(() => {
    if (!isNaN(serviceEmailId)) {
      api.serviceEmail.detail(serviceEmailId).then(data => {
        setValues({
          ...data,
          recipients: data.recipients.split(';').join('\n'),
        })
      })
    }
  }, [serviceEmailId])

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleChangeDirect = (prop, value) => {
    setValues({ ...values, [prop]: value })
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.serviceEmail.delete(serviceEmailId).then(() => {
        enqueueSnackbar(t('Service email deleted'), { variant: 'success' })
        navigate(`/services/${serviceId}`)
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service email'), { variant: 'error' })
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!isValidCron(values.cronExpression)) {
      enqueueSnackbar(t('Invalid cron expression'), { variant: 'error' })
      return
    }
    const formattedRecipients = values.recipients.split('\n').map(email => email.trim()).filter(email => email.length > 0).join(';')
    const data = {
      ...values,
      recipients: formattedRecipients,
      serviceId: serviceId,
    }
    try {
      let serviceEmail = await api.serviceEmail.save(data)
      navigate(`/services/${serviceId}/email/${serviceEmail.id}`)
      enqueueSnackbar(t('Service email saved'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service email'), { variant: 'error' })
    }
  }

  function isValidCron(expression) {
    try {
      CronExpressionParser.parse(expression)
      return true
    } catch (e) {
      return false
    }
  }

  const cronPresets = [
    { label: 'Každý deň o 8:00', value: '0 8 * * *' },
    { label: 'Každý pracovný deň o 7:30', value: '30 7 * * 1-5' },
    { label: 'Každý pondelok o 9:00', value: '0 9 * * 1' },
    { label: '1. deň v mesiaci o 10:00', value: '0 10 1 * *' },
  ]

  return (
    <Page title={t('Service email')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumb-service-email'}
                         title={values?.name ?? t('Service email')}
                         items={[
                           { title: t('Services'), href: `/services` },
                           { title: service?.title ?? t('Service'), href: `/services/${serviceId}` }
                         ]}/>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12}>
                <ShortFormControl name={'cronExpression'} title={t('Cron expression')} value={values.cronExpression} handleChange={handleChange}/>
                <Stack direction={'row'} spacing={2} flexWrap="wrap" mt={1}>
                  {cronPresets.map((preset, index) => (
                      <Typography key={index} variant="caption">
                        <Link href="#" onClick={(e) => {
                          e.preventDefault()
                          handleChangeDirect('cronExpression', preset.value)
                        }}>
                          {preset.label}
                        </Link>
                      </Typography>
                  ))}
                </Stack>
                <Stack direction={'row'} justifyContent={'end'}>
                  <Typography variant={'caption'}>{t('helperServiceEmailCronExpressionLinks')}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="standard" fullWidth>
                  <TextField
                    id="email-recipients"
                    label={t('Recipients')}
                    multiline
                    value={values.recipients}
                    onChange={(e) => setValues(prev => ({ ...prev, recipients: e.target.value }))}
                    fullWidth
                    variant="outlined" />
                </FormControl>
                <Stack direction={'row'} justifyContent={'end'}>
                  <Typography variant={'caption'}>{t('helperServiceEmailRecipients')}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox/>} checked={values.onlyCorrectAnswers} onChange={(e) => {
                    handleChangeDirect('onlyCorrectAnswers', e.target.checked)
                  }} label={t('Only send correct answers')}/>
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'} disabled={user?.readonly}>{t('Save')}</Button>
            { !isNaN(serviceEmailId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} disabled={user?.readonly} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
          </Box>
        </Grid>
        <AlertSimpleDialog
          title={t('Delete service email')}
          message={t('delete-confirm-msg', { name: values.id })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
      </Container>
    </Page>
  )
}